import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react'
import {
  legacyScheduleBatchOfficeOptions,
  legacyScheduleMemoPresets,
  type LegacyScheduleMemoPreset,
} from '../../mock/legacyScheduleBatch'
import './LegacyScheduleMemoPreset.css'

type S06ModalState = {
  message: string
  alertOnly?: boolean
  onConfirm?: () => void
} | null

type S06PresetBridge = {
  getPresets: () => LegacyScheduleMemoPreset[]
  savePreset: (preset: LegacyScheduleMemoPreset) => void
  reorderPresets: (ids: string[]) => void
  refresh: () => void
}

type S06BridgeWindow = Window & { __LMEDRP_S06_PRESET_BRIDGE__?: S06PresetBridge }

const pageSizeOptions = ['30', '60', '100', '500', '1000'] as const

function getOpenerBridge() {
  try {
    return (window.opener as S06BridgeWindow | null)?.__LMEDRP_S06_PRESET_BRIDGE__
  } catch {
    return undefined
  }
}

function replaceQuery(updates: Record<string, string>) {
  const url = new URL(window.location.href)
  Object.entries(updates).forEach(([key, value]) => url.searchParams.set(key, value))
  window.history.replaceState({}, '', `${url.pathname}?${url.searchParams.toString()}`)
}

function openPresetPopup(path: string, width: number, height: number) {
  window.open(path, '_blank', `popup=yes,width=${width},height=${height},resizable=yes,scrollbars=yes`)
}

function S06Modal({ state, onClose }: { state: S06ModalState; onClose: () => void }) {
  if (!state) return null

  return <div id="g_question_modal" className="g_question_modal s06-modal" role="dialog" aria-modal="true" aria-label={state.message}>
    <div className="mini_modal">
      <p className="question">{state.message}</p>
      <div className="flex_wrap gap_10 g_modal_button_wrap">
        <button
          id="g_yes_btn"
          className="g_btn modalConfirm save"
          type="button"
          autoFocus
          onClick={() => state.onConfirm ? state.onConfirm() : onClose()}
        >{state.alertOnly ? 'Ok' : '예(Yes)'}</button>
        {!state.alertOnly && <button id="g_no_btn" className="g_btn modalConfirm close" type="button" onClick={onClose}>아니오(No)</button>}
      </div>
    </div>
  </div>
}

function S06OfficeChosen({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [highlightedId, setHighlightedId] = useState(value)
  const office = legacyScheduleBatchOfficeOptions.find((item) => item.id === value) ?? legacyScheduleBatchOfficeOptions[0]
  const options = legacyScheduleBatchOfficeOptions.filter((item) => item.name.toLowerCase().includes(search.trim().toLowerCase()))

  useEffect(() => {
    if (options.some((item) => item.id === highlightedId)) return
    setHighlightedId(options[0]?.id ?? '')
  }, [highlightedId, options])

  const choose = (nextId: string) => {
    onChange(nextId)
    setHighlightedId(nextId)
    setOpen(false)
    setSearch('')
  }

  const moveHighlight = (step: number) => {
    if (!options.length) return
    const currentIndex = options.findIndex((item) => item.id === highlightedId)
    const start = currentIndex < 0 ? (step > 0 ? -1 : 0) : currentIndex
    const nextIndex = Math.max(0, Math.min(options.length - 1, start + step))
    setHighlightedId(options[nextIndex].id)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      if (open) event.preventDefault()
      setOpen(false)
      setSearch('')
      return
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!open) {
        setOpen(true)
        setHighlightedId(office.id)
      } else {
        moveHighlight(event.key === 'ArrowDown' ? 1 : -1)
      }
      return
    }
    if (event.key === 'Enter' && open) {
      event.preventDefault()
      const selected = options.find((item) => item.id === highlightedId) ?? options[0]
      if (selected) choose(selected.id)
    }
  }

  return <>
    <select
      id="office_select"
      className="g_input chosen-select width_2x l_office_select"
      value={office.id}
      onChange={(event) => onChange(event.target.value)}
      tabIndex={-1}
      aria-hidden="true"
      style={{ display: 'none' }}
    >
      {legacyScheduleBatchOfficeOptions.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
    </select>
    <div
      id="office_select_chosen"
      className={`chosen-container chosen-container-single${open ? ' chosen-with-drop chosen-container-active' : ''}`}
      onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false) }}
      onKeyDown={handleKeyDown}
    >
      <button className="chosen-single" type="button" aria-haspopup="listbox" aria-expanded={open} onClick={() => { setOpen((current) => !current); setHighlightedId(office.id) }}>
        <span>{office.name}</span><div><b /></div>
      </button>
      <div className="chosen-drop">
        <div className="chosen-search"><input className="chosen-search-input" type="text" aria-label="지점 검색" aria-activedescendant={highlightedId ? `office-option-${highlightedId}` : undefined} value={search} onChange={(event) => setSearch(event.target.value)} /></div>
        <ul className="chosen-results" role="listbox">
          {options.map((item) => <li
            id={`office-option-${item.id}`}
            key={item.id}
            className={`active-result${item.id === office.id ? ' result-selected' : ''}${item.id === highlightedId ? ' highlighted' : ''}`}
            role="option"
            aria-selected={item.id === office.id}
            onMouseDown={(event) => event.preventDefault()}
            onMouseEnter={() => setHighlightedId(item.id)}
            onClick={() => choose(item.id)}
          >{item.name}</li>)}
          {!options.length && <li className="no-results">No results match <span>{search}</span></li>}
        </ul>
      </div>
    </div>
  </>
}

function VisibilityTabs({ hidden, onChange, extraClass = '' }: { hidden: boolean; onChange?: (hidden: boolean) => void; extraClass?: string }) {
  return <div className={`g_tab l_is_show_func${extraClass ? ` ${extraClass}` : ''}`}>
    <button id="is_show_btn" type="button" className="g_btn category tab g_tab_item" data-selected={!hidden} onClick={() => onChange?.(false)}>표시</button>
    <button id="no_show_btn" type="button" className="g_btn category tab g_tab_item" data-selected={hidden} onClick={() => onChange?.(true)}>미표시</button>
  </div>
}

export function LegacyScheduleMemoPreset() {
  const initialParams = new URLSearchParams(window.location.search)
  const requestedOffice = initialParams.get('office_id')
  const initialOffice = legacyScheduleBatchOfficeOptions.some((item) => item.id === requestedOffice)
    ? requestedOffice as string
    : legacyScheduleBatchOfficeOptions[0].id
  const initialPageSize = pageSizeOptions.includes(initialParams.get('page_count') as typeof pageSizeOptions[number])
    ? initialParams.get('page_count') as typeof pageSizeOptions[number]
    : '30'

  const [officeId, setOfficeId] = useState(initialOffice)
  const [hidden, setHidden] = useState(initialParams.get('is_show') === '1')
  const [pageCount, setPageCount] = useState<string>(initialPageSize)
  const [pageNo, setPageNo] = useState(Math.max(1, Number(initialParams.get('page_no')) || 1))
  const [presets, setPresets] = useState<LegacyScheduleMemoPreset[]>(legacyScheduleMemoPresets)
  const [checked, setChecked] = useState<string[]>([])
  const [modal, setModal] = useState<S06ModalState>(null)
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null)

  useEffect(() => {
    document.title = 'LMEDRP'
    const url = new URL(window.location.href)
    const defaults: Record<string, string> = {
      page_start: '1',
      page_count: initialPageSize,
      page_no: '1',
      is_show: hidden ? '1' : '0',
      office_id: initialOffice,
    }
    let changed = false
    Object.entries(defaults).forEach(([key, value]) => {
      if (!url.searchParams.has(key) || (key === 'office_id' && url.searchParams.get(key) !== initialOffice)) {
        url.searchParams.set(key, value)
        changed = true
      }
    })
    if (changed) window.history.replaceState({}, '', `${url.pathname}?${url.searchParams.toString()}`)
  }, [hidden, initialOffice, initialPageSize])

  useEffect(() => {
    const target = window as S06BridgeWindow
    target.__LMEDRP_S06_PRESET_BRIDGE__ = {
      getPresets: () => presets.map((item) => ({ ...item })),
      savePreset: (preset) => setPresets((current) => {
        const exists = current.some((item) => item.id === preset.id)
        return exists ? current.map((item) => item.id === preset.id ? { ...preset } : item) : [{ ...preset }, ...current]
      }),
      reorderPresets: (ids) => setPresets((current) => {
        const byId = new Map(current.map((item) => [item.id, item]))
        const ordered = ids.map((id) => byId.get(id)).filter((item): item is LegacyScheduleMemoPreset => Boolean(item))
        const untouched = current.filter((item) => !ids.includes(item.id))
        return [...ordered, ...untouched]
      }),
      refresh: () => setPresets((current) => [...current]),
    }
    return () => { delete target.__LMEDRP_S06_PRESET_BRIDGE__ }
  }, [presets])

  const allRows = useMemo(() => presets.filter((row) => row.officeId === officeId && row.visible !== hidden), [hidden, officeId, presets])
  const perPage = Number(pageCount)
  const pageTotal = Math.max(1, Math.ceil(allRows.length / perPage))
  const safePageNo = Math.min(pageNo, pageTotal)
  const offset = (safePageNo - 1) * perPage
  const rows = allRows.slice(offset, offset + perPage)
  // The Django pagination helper displays the requested range even when the
  // final page contains fewer rows (and even when the list is empty).
  const dataStart = offset + 1
  const dataEnd = offset + perPage

  const changeOffice = (nextOfficeId: string) => {
    setOfficeId(nextOfficeId)
    setChecked([])
    setPageNo(1)
    replaceQuery({ office_id: nextOfficeId, page_start: '1', page_count: pageCount, page_no: '1', is_show: hidden ? '1' : '0' })
  }

  const changeTab = (nextHidden: boolean) => {
    setHidden(nextHidden)
    setChecked([])
    setPageNo(1)
    replaceQuery({ office_id: officeId, is_show: nextHidden ? '1' : '0', page_start: '1', page_count: pageCount, page_no: '1' })
  }

  const changePageSize = (nextSize: string) => {
    setPageCount(nextSize)
    setPageNo(1)
    setChecked([])
    replaceQuery({ page_start: '1', page_count: nextSize, page_no: '1', office_id: officeId, is_show: hidden ? '1' : '0' })
  }

  const changePage = (nextPage: number) => {
    setPageNo(nextPage)
    setChecked([])
    replaceQuery({ page_start: String((nextPage - 1) * perPage + 1), page_count: pageCount, page_no: String(nextPage), office_id: officeId, is_show: hidden ? '1' : '0' })
  }

  const toggleAll = () => setChecked(checked.length === rows.length ? [] : rows.map((row) => row.id))
  const requestVisibilityChange = () => {
    if (!checked.length) {
      setModal({ message: '한 개 이상의 항목을 선택해 주세요.', alertOnly: true, onConfirm: () => setModal(null) })
      return
    }
    const action = hidden ? '표시' : '미표시'
    const selectedIds = [...checked]
    setModal({
      message: `${action} 하시겠습니까?`,
      onConfirm: () => {
        setModal({
          message: `선택하신 항목이 ${action} 처리되었습니다.`,
          alertOnly: true,
          onConfirm: () => {
            setPresets((current) => current.map((item) => selectedIds.includes(item.id) ? { ...item, visible: hidden } : item))
            setChecked([])
            setModal(null)
          },
        })
      },
    })
  }

  const openEdit = (row: LegacyScheduleMemoPreset) => {
    if (hidden) return
    openPresetPopup(`/v1/schedule_memo_preset/save/?CRUD=3&detail_id=${encodeURIComponent(row.id)}&office_id=${encodeURIComponent(officeId)}&is_show=0&caller=schedule_memo_setting`, 500, 500)
  }

  return <div className="legacy-s06">
    <h1 className="g_title">고객 내용 프리셋 설정</h1>
    <aside className="g_filter_wrap">
      <div className="g_input_wrap">
        <label className="g_label title center" htmlFor="office_select">지점</label>
        <S06OfficeChosen value={officeId} onChange={changeOffice} />
      </div>
    </aside>

    <form id="l_setting_cancel_form" onSubmit={(event) => { event.preventDefault(); requestVisibilityChange() }}>
      <VisibilityTabs hidden={hidden} extraClass="l_non_display" />
      <div className="g_row l_row_func">
        <div className="l_row_pagination">
          <div id="g_page_limit_wrap" className="g_page_limit">
            Data: <span className="l_start_num">{dataStart}</span> to <span className="l_end_num">{dataEnd}</span> (전체 : <span className="l_total_count">{allRows.length}</span>)
            {' '}
            <select id="g_limit_data" className="g_input" aria-label="페이지 크기" value={pageCount} onChange={(event) => changePageSize(event.target.value)}>
              {pageSizeOptions.map((size) => <option key={size} value={size}>{size}</option>)}
            </select>
          </div>
        </div>
        <VisibilityTabs hidden={hidden} onChange={changeTab} />
        <div className="gab" />
        <div id="l_item_control" className="g_btn_wrap right">
          <button id="l_btn_cancel" className="g_btn g_is_show g_btn_submit" type="submit">선택된 것 "{hidden ? '표시' : '미표시'}"</button>
          {!hidden && <>
            {' '}
            <button id="l_btn_order_by" className="g_btn modify" type="button" onClick={() => openPresetPopup(`/v1/schedule_memo_preset/orderby/?CRUD=3&office_id=${encodeURIComponent(officeId)}&un_limit=1&caller=schedule_memo_setting`, 1080, 800)}>정렬 편집</button>
            {' '}
            <button id="l_btn_add" className="g_btn add" type="button" onClick={() => openPresetPopup(`/v1/schedule_memo_preset/save/?CRUD=1&office_id=${encodeURIComponent(officeId)}&caller=schedule_memo_setting`, 500, 500)}>추가</button>
          </>}
        </div>
      </div>

      <article className="g_list_wrap g_scroll_bar">
        <table id="l_table_list" className="g_list l_table">
          <colgroup>
            <col style={{ width: 123.828125 }} /><col style={{ width: 105.390625 }} /><col style={{ width: 274.140625 }} /><col style={{ width: 486.46875 }} /><col style={{ width: 169.171875 }} />
          </colgroup>
          <thead><tr>
            <th className="g_list_index">NO</th>
            <th><input id="l_cancel_check_all" className="g_checked_toggle_all g_input g_checkbox" type="checkbox" aria-label="전체 선택" checked={rows.length > 0 && checked.length === rows.length} onChange={toggleAll} /></th>
            <th>프리셋 이름</th><th>메모 내용</th><th>표시</th>
          </tr></thead>
          <tbody>{rows.map((row, index) => <PresetRow
            key={row.id}
            row={row}
            index={offset + index}
            hidden={hidden}
            checked={checked.includes(row.id)}
            onCheck={() => setChecked((current) => current.includes(row.id) ? current.filter((id) => id !== row.id) : [...current, row.id])}
            onEdit={() => openEdit(row)}
            onTooltip={(next) => setTooltip(next)}
          />)}</tbody>
        </table>
      </article>
    </form>

    <div className="l_page_func"><ul id="g_pagination" className="g_pagination">
      {Array.from({ length: pageTotal }, (_, index) => index + 1).map((page) => <li key={page} className={page === safePageNo ? 'selected' : ''}><button type="button" className={`g_btn pagination${page === safePageNo ? ' active' : ''}`} onClick={() => changePage(page)}>{page}</button></li>)}
    </ul></div>
    {tooltip && <div className="ui-tooltip ui-widget ui-widget-content s06-tooltip" role="tooltip" style={{ left: tooltip.x, top: tooltip.y + 15 }}>{tooltip.text}</div>}
    <S06Modal state={modal} onClose={() => setModal(null)} />
  </div>
}

function PresetRow({ row, index, hidden, checked, onCheck, onEdit, onTooltip }: {
  row: LegacyScheduleMemoPreset
  index: number
  hidden: boolean
  checked: boolean
  onCheck: () => void
  onEdit: () => void
  onTooltip: (tooltip: { text: string; x: number; y: number } | null) => void
}) {
  const itemKeyboard = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onEdit() }
  }
  const itemProps = hidden ? {} : { role: 'button', tabIndex: 0, onClick: onEdit, onKeyDown: itemKeyboard }

  return <tr className="l_data_row">
    <td><span id="l_index">{index + 1}</span></td>
    <td className="l_cancel_check_wrap"><input className="g_input g_checkbox g_checked_toggle l_cancel_box" type="checkbox" aria-label={`${row.name} 선택`} checked={checked} onChange={onCheck} /></td>
    <td><span id="l_preset_name" className={hidden ? undefined : 'g_item'} {...itemProps}>{row.name}</span></td>
    <td className="left l_td_preset_customer_content"><span
      id="l_preset_customer_content"
      className={hidden ? undefined : 'g_item'}
      data-g-tooltip={row.content}
      onMouseMove={(event) => onTooltip({ text: row.content, x: event.clientX, y: event.clientY })}
      onMouseLeave={() => onTooltip(null)}
      {...itemProps}
    >{row.content}</span></td>
    <td><span id="l_data_display">미표시</span></td>
  </tr>
}

export function LegacyScheduleMemoPresetSave() {
  const query = new URLSearchParams(window.location.search)
  const isEdit = query.get('CRUD') === '3'
  const detailId = query.get('detail_id') ?? ''
  const officeId = query.get('office_id') ?? legacyScheduleBatchOfficeOptions[0].id
  const office = legacyScheduleBatchOfficeOptions.find((item) => item.id === officeId) ?? legacyScheduleBatchOfficeOptions[0]
  const bridgeSource = getOpenerBridge()?.getPresets().find((item) => item.id === detailId)
  const source = bridgeSource ?? legacyScheduleMemoPresets.find((item) => item.id === detailId) ?? legacyScheduleMemoPresets.find((item) => item.officeId === office.id) ?? legacyScheduleMemoPresets[0]
  const generatedId = useRef(`mock-preset-new-${Date.now()}`)
  const [name, setName] = useState(isEdit ? source.name : '')
  const [content, setContent] = useState(isEdit ? source.content : '')
  const [modal, setModal] = useState<S06ModalState>(null)

  useEffect(() => {
    document.title = 'LMEDRP'
    try { window.resizeTo(500, 500) } catch { /* Browser policy may block resizing a normal tab. */ }
  }, [])

  const closeAfterSave = () => {
    getOpenerBridge()?.refresh()
    try { window.opener?.focus() } catch { /* Same-origin opener is optional. */ }
    window.close()
  }

  const save = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmedName = name.trim()
    if (!trimmedName) {
      setName('')
      window.setTimeout(() => document.querySelector<HTMLInputElement>('#l_setting_name')?.reportValidity(), 0)
      return
    }
    setModal({
      message: isEdit ? '변경 내용을 저장하시겠습니까?' : '저장하시겠습니까?',
      onConfirm: () => {
        getOpenerBridge()?.savePreset({ id: isEdit ? source.id : generatedId.current, officeId: office.id, name: trimmedName, content, visible: source?.visible ?? true })
        setModal({ message: '저장되었습니다.', alertOnly: true, onConfirm: closeAfterSave })
      },
    })
  }

  return <div className="legacy-s06-popup">
    <h1 className="g_title l_save_title">고객 내용 프리셋 {isEdit ? '수정' : '추가'}</h1>
    <form id="l_setting_save_form" className="l_save_form" data-type={isEdit ? '3' : '1'} onSubmit={save}>
      <section className="g_card l_section_save">
        <PopupRow label="지점" required><input id="office_select" className="g_input" readOnly value={office.name} /></PopupRow>
        <PopupRow label="프리셋 이름" required><input id="l_setting_name" className="g_input width_2x" name="preset_name" value={name} onChange={(event) => setName(event.target.value)} required /></PopupRow>
        <PopupRow label="메모 내용"><textarea id="l_setting_content" className="g_input width_2x l_preset_content" name="preset_customer_content" value={content} onChange={(event) => setContent(event.target.value)} required /></PopupRow>
      </section>
      <div className="g_func_wrap">
        <button className="g_btn save g_btn_submit" type="submit">저장</button>
        <button id="l_close_form" className="g_btn" type="button" onClick={() => window.close()}>닫기</button>
      </div>
    </form>
    <S06Modal state={modal} onClose={() => setModal(null)} />
  </div>
}

export function LegacyScheduleMemoPresetOrder() {
  const query = new URLSearchParams(window.location.search)
  const officeId = query.get('office_id') ?? legacyScheduleBatchOfficeOptions[0].id
  const office = legacyScheduleBatchOfficeOptions.find((item) => item.id === officeId) ?? legacyScheduleBatchOfficeOptions[0]
  const [rows, setRows] = useState<LegacyScheduleMemoPreset[]>(() => {
    const openerRows = getOpenerBridge()?.getPresets()
    return (openerRows?.length ? openerRows : legacyScheduleMemoPresets).filter((item) => item.officeId === office.id && item.visible).map((item) => ({ ...item }))
  })
  const [dragId, setDragId] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)
  const [modal, setModal] = useState<S06ModalState>(null)
  const allowClose = useRef(false)

  useEffect(() => {
    document.title = 'LMEDRP'
    try { window.resizeTo(1080, 800) } catch { /* Browser policy may block resizing a normal tab. */ }
  }, [])

  const closeWindow = () => {
    allowClose.current = true
    try { window.opener?.focus() } catch { /* Same-origin opener is optional. */ }
    window.close()
  }

  const drop = (targetId: string) => {
    if (!dragId || dragId === targetId) { setDragId(null); return }
    setRows((current) => {
      const next = [...current]
      const from = next.findIndex((item) => item.id === dragId)
      const to = next.findIndex((item) => item.id === targetId)
      if (from < 0 || to < 0) return current
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      return next
    })
    setDirty(true)
    setDragId(null)
  }

  const requestClose = () => {
    if (!dirty) { closeWindow(); return }
    setModal({ message: '변경 내용을 저장하지 않고 나가시겠습니까?', onConfirm: closeWindow })
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setModal({
      message: '정렬을 변경하시겠습니까?',
      onConfirm: () => {
        getOpenerBridge()?.reorderPresets(rows.map((item) => item.id))
        setDirty(false)
        setModal({ message: '순서가 변경되었습니다', alertOnly: true, onConfirm: closeWindow })
      },
    })
  }

  return <div className="legacy-s06-popup legacy-s06-order">
    <div className="g_input_wrap">
      <span className="g_label title center">지점</span>
      <input id="office_select" className="g_input" readOnly value={office.name} />
    </div>
    <form id="l_setting_order_form" onSubmit={submit}>
      <article className="g_list_wrap g_scroll_bar">
        <table id="l_table_list" className="g_list l_table">
          <colgroup><col /><col /><col /></colgroup>
          <thead><tr><th /><th>프리셋 이름</th><th>메모 내용</th></tr></thead>
          <tbody>{rows.map((row) => <tr
            className="l_data_row l_sort_data"
            key={row.id}
            onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'move' }}
            onDrop={(event) => { event.preventDefault(); drop(row.id) }}
          >
            <td
              id="l_sort"
              draggable
              role="button"
              tabIndex={0}
              aria-label={`${row.name} 순서 이동`}
              onDragStart={(event) => { setDragId(row.id); event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', row.id) }}
              onDragEnd={() => setDragId(null)}
            >⇅</td>
            <td><span id="l_setting_name" className="l_list_item">{row.name}</span></td>
            <td><span id="l_setting_content" className="l_list_item">{row.content}</span></td>
          </tr>)}</tbody>
        </table>
      </article>
      <div className="g_func_wrap">
        <button className="g_btn save g_btn_submit" type="submit">저장</button>
        <button id="l_close_form" className="g_btn l_close_form" type="button" onClick={requestClose}>닫기</button>
      </div>
    </form>
    <S06Modal state={modal} onClose={() => setModal(null)} />
  </div>
}

function PopupRow({ label, children, required = false }: { label: string; children: ReactNode; required?: boolean }) {
  return <div className={`g_input_wrap${required ? ' required' : ''}`}><span className="g_label title center">{label}</span>{children}</div>
}
